import clsx from 'clsx';
import { MapPin } from 'lucide-react';

export interface ParkFactorData {
  venueId: number;
  venueName: string;
  runFactor: number;
  hrFactor: number;
  hitFactor: number;
  isOutdoor: boolean;
  roofType?: string;
  dimensions?: {
    leftField?: number;
    centerField?: number;
    rightField?: number;
  };
  elevationFt?: number;
  offensiveRating?: string;
}

interface Props {
  parkData?: ParkFactorData;
  venueName?: string;
}

function FactorBar({ label, value, inverse }: { label: string; value: number; inverse?: boolean }) {
  // 1.0 = league average, displayed as 100. Bars color based on direction.
  const pct = (value - 0.70) / 0.60; // maps 0.70–1.30 to 0–100%
  const cappedPct = Math.min(1, Math.max(0, pct));
  const isElevated = value >= 1.04;
  const isSuppressed = value <= 0.96;
  const isNeutral = !isElevated && !isSuppressed;

  const barColor = inverse
    ? isSuppressed ? 'bg-win' : isElevated ? 'bg-loss' : 'bg-gray-500'
    : isElevated ? 'bg-warn' : isSuppressed ? 'bg-brand' : 'bg-gray-500';

  const labelColor = isElevated ? 'text-warn' : isSuppressed ? 'text-brand' : 'text-gray-400';

  return (
    <div className="space-y-1">
      <div className="flex items-center justify-between">
        <span className="text-xs text-gray-400">{label}</span>
        <span className={clsx('text-xs font-mono font-semibold', labelColor)}>
          {(value * 100).toFixed(0)}
        </span>
      </div>
      <div className="h-1.5 bg-surface-4 rounded-full overflow-hidden">
        <div
          className={clsx('h-full rounded-full transition-all', barColor)}
          style={{ width: `${cappedPct * 100}%` }}
        />
      </div>
      {isNeutral && <div className="text-[9px] text-gray-600">League avg</div>}
    </div>
  );
}

const OFFENSIVE_RATING_LABELS: Record<string, { label: string; color: string }> = {
  extreme_hitters:   { label: 'Extreme Hitters\' Park', color: 'text-loss' },
  hitter_friendly:   { label: 'Hitter Friendly',        color: 'text-warn' },
  neutral:           { label: 'Neutral Park',            color: 'text-gray-400' },
  pitcher_friendly:  { label: 'Pitcher Friendly',        color: 'text-brand' },
  extreme_pitchers:  { label: 'Extreme Pitchers\' Park', color: 'text-info' },
};

export function ParkFactors({ parkData, venueName }: Props) {
  if (!parkData) {
    return (
      <div className="card p-4 space-y-2">
        <div className="text-xs font-medium text-gray-400 uppercase tracking-wider flex items-center gap-1.5">
          <MapPin className="w-3 h-3" />
          Park Factors
        </div>
        <div className="text-sm text-gray-600">{venueName ?? 'Venue data unavailable'}</div>
        <div className="text-xs text-gray-600">Park factor data not available for this venue.</div>
      </div>
    );
  }

  const ratingConfig = OFFENSIVE_RATING_LABELS[parkData.offensiveRating ?? 'neutral'];

  return (
    <div className="card p-4 space-y-4">
      <div className="text-xs font-medium text-gray-400 uppercase tracking-wider flex items-center gap-1.5">
        <MapPin className="w-3 h-3" />
        Park Factors
      </div>

      <div className="flex items-start justify-between">
        <div>
          <div className="text-sm font-semibold text-gray-100">{parkData.venueName}</div>
          <div className={clsx('text-xs mt-0.5', ratingConfig?.color ?? 'text-gray-400')}>
            {ratingConfig?.label ?? 'Neutral'}
          </div>
        </div>
        <div className="text-right text-xs text-gray-500 space-y-0.5">
          <div>{parkData.isOutdoor ? 'Outdoor' : 'Indoor'}</div>
          {parkData.roofType && parkData.roofType !== 'open' && (
            <div className="capitalize">{parkData.roofType} roof</div>
          )}
          {parkData.elevationFt && (
            <div>{parkData.elevationFt.toLocaleString()}ft elevation</div>
          )}
        </div>
      </div>

      <div className="space-y-2.5 pt-2 border-t border-surface-4">
        <div className="text-[10px] text-gray-600 uppercase tracking-wider mb-1.5">Factor Index (100 = league avg)</div>
        <FactorBar label="Run Factor" value={parkData.runFactor} />
        <FactorBar label="HR Factor" value={parkData.hrFactor} />
        <FactorBar label="Hit Factor" value={parkData.hitFactor} />
      </div>

      {parkData.dimensions && (
        <div className="pt-2 border-t border-surface-4">
          <div className="text-[10px] text-gray-600 uppercase tracking-wider mb-1.5">Dimensions (ft)</div>
          <div className="flex items-center gap-4 text-xs text-gray-400 font-mono">
            {parkData.dimensions.leftField && <span>LF {parkData.dimensions.leftField}</span>}
            {parkData.dimensions.centerField && <span>CF {parkData.dimensions.centerField}</span>}
            {parkData.dimensions.rightField && <span>RF {parkData.dimensions.rightField}</span>}
          </div>
        </div>
      )}
    </div>
  );
}
